import type { PreventiveCareAcknowledgementResponse } from '@emr/shared';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  listPreventiveCareAcknowledgements,
  listPreventiveCareRecommendations,
} from '../../../api/preventiveCare.ts';
import { LoadingNotice } from '../../../components/AsyncState.tsx';
import { QueryFailureNotice } from '../QueryFailureNotice.tsx';
import { PreventiveCareCard } from './PreventiveCareCard.tsx';

/**
 * Preventive-care-gap reminders (`BACKLOG.md` C12) — this product's first Clinical Decision Support
 * Intervention, and previously `server`/`shared`-only (see that item's own "no web UI surfaces this
 * yet" follow-up). Cards, not a `ChartTable` row, matching `NoteCard.tsx`'s own precedent: each
 * recommendation carries a non-directive summary, a full DSI source-attribution disclosure, and up
 * to three real actions (acknowledge/mark-ordered/dismiss), none of which fit a table cell.
 *
 * Two independent queries, not one: `GET .../preventive-care-recommendations` (every currently-open
 * gap, recomputed live) and `GET .../preventive-care-recommendations/acknowledgements` (the full
 * decision history). Both are needed together — a card has to know not just that a gap is open, but
 * whether a clinician already recorded a decision about it, since acting on a recommendation never
 * closes it here (`preventiveCareRoutes.ts`'s own header on why this feature's only write never
 * touches `immunizations`).
 */
export function PreventiveCareTab({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('patientChartTabs');

  const recommendations = useQuery({
    queryKey: ['patient', patientId, 'preventiveCareRecommendations'],
    queryFn: ({ signal }) => listPreventiveCareRecommendations(patientId, signal),
  });
  const acknowledgements = useQuery({
    queryKey: ['patient', patientId, 'preventiveCareAcknowledgements'],
    queryFn: ({ signal }) => listPreventiveCareAcknowledgements(patientId, signal),
  });

  if (recommendations.isPending || acknowledgements.isPending) {
    return <LoadingNotice label={t('preventiveCare.loading')} />;
  }
  if (recommendations.isError) {
    return (
      <QueryFailureNotice
        error={recommendations.error}
        onRetry={() => void recommendations.refetch()}
      />
    );
  }
  if (acknowledgements.isError) {
    return (
      <QueryFailureNotice
        error={acknowledgements.error}
        onRetry={() => void acknowledgements.refetch()}
      />
    );
  }

  const latestAckByRuleId = latestAcknowledgementsByRuleId(acknowledgements.data);

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-ink-muted">{t('preventiveCare.intro')}</p>
      {recommendations.data.length === 0 ? (
        <p className="m-0 text-sm text-ink">{t('preventiveCare.empty')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {recommendations.data.map((recommendation) => (
            <PreventiveCareCard
              key={recommendation.ruleId}
              patientId={patientId}
              recommendation={recommendation}
              latestAcknowledgement={latestAckByRuleId.get(recommendation.ruleId) ?? null}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** The most recent acknowledgement per `ruleId`, from a list the service already returns newest
 * first (`preventiveCareService.ts`'s `listPreventiveCareAcknowledgements`) — compared explicitly by
 * `createdAt` rather than trusting that ordering here too, so this stays correct even if the server
 * ordering ever changes. */
function latestAcknowledgementsByRuleId(
  acknowledgements: readonly PreventiveCareAcknowledgementResponse[],
): ReadonlyMap<string, PreventiveCareAcknowledgementResponse> {
  const byRuleId = new Map<string, PreventiveCareAcknowledgementResponse>();
  for (const ack of acknowledgements) {
    const current = byRuleId.get(ack.ruleId);
    if (current === undefined || ack.createdAt > current.createdAt) {
      byRuleId.set(ack.ruleId, ack);
    }
  }
  return byRuleId;
}
